import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronLeft, ChevronRight, CheckCircle2, MapPin, Building2, Database, Play } from 'lucide-react';
import { WIZARD } from '@/constants/testIds';
import AddressPicker from '@/components/AddressPicker';

const STEPS = [
  { key: 'scope', label: 'Tipo y ubicación', icon: MapPin },
  { key: 'business', label: 'Perfil del negocio', icon: Building2 },
  { key: 'sources', label: 'Fuentes', icon: Database },
  { key: 'confirm', label: 'Generar', icon: Play },
];

const UNIT_TYPES = [
  { v: 'sucursal_propia', l: 'Sucursal propia' },
  { v: 'franquicia', l: 'Franquicia' },
  { v: 'distribuidor', l: 'Distribuidor' },
  { v: 'punto_venta', l: 'Punto de venta' },
  { v: 'showroom', l: 'Showroom' },
  { v: 'otro', l: 'Otro' },
];

export default function NewAnalysis() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [states, setStates] = useState([]);
  const [munis, setMunis] = useState([]);
  const [regions, setRegions] = useState([]);
  const [sources, setSources] = useState([]);

  const [analysisName, setAnalysisName] = useState('');
  const [scope, setScope] = useState('municipio');
  const [stateCode, setStateCode] = useState('');
  const [selectedMunis, setSelectedMunis] = useState([]); // [{code, name}]
  const [regionId, setRegionId] = useState('');
  const [radius, setRadius] = useState(5);
  const [notes, setNotes] = useState('');

  // Address + radius flow (scope=direccion) — picker maneja autocomplete + mapa
  const [addressPick, setAddressPick] = useState(null); // {place_id, name, address, lat, lon, type_detected}

  const [biz, setBiz] = useState({
    project_name: '', company_name: '', industry: '', subcategory: '',
    description: '', unit_type: 'sucursal_propia',
    target_customer: '', age_range: '', gender: '', socioeconomic: '',
    avg_ticket: '', gross_margin: '', max_investment: '', max_rent: '',
    sqm_desired: '', employees_estimated: '',
    min_monthly_sales: '', target_monthly_sales: '',
    needs_parking: false, depends_foot_traffic: false, depends_vehicle_traffic: false,
    depends_tourism: false, depends_digital: false,
    known_competitors: '', keywords: '',
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/catalog/states').then((r) => setStates(r.data));
    api.get('/catalog/regions').then((r) => setRegions(r.data));
    api.get('/catalog/sources').then((r) => setSources(r.data));
    // Prefill from saved business profile if present
    const raw = localStorage.getItem('strategia_prefill_profile');
    if (raw) {
      try {
        const p = JSON.parse(raw);
        setBiz({
          project_name: p.project_name || '',
          company_name: p.company_name || '',
          industry: p.industry || '',
          subcategory: p.subcategory || '',
          description: p.description || '',
          unit_type: p.unit_type || 'sucursal_propia',
          target_customer: p.target_customer || '',
          age_range: p.age_range || '',
          gender: p.gender || '',
          socioeconomic: p.socioeconomic || '',
          avg_ticket: p.avg_ticket ?? '',
          gross_margin: p.gross_margin ?? '',
          max_investment: p.max_investment ?? '',
          max_rent: p.max_rent ?? '',
          sqm_desired: p.sqm_desired ?? '',
          employees_estimated: p.employees_estimated ?? '',
          min_monthly_sales: p.min_monthly_sales ?? '',
          target_monthly_sales: p.target_monthly_sales ?? '',
          needs_parking: !!p.needs_parking,
          depends_foot_traffic: !!p.depends_foot_traffic,
          depends_vehicle_traffic: !!p.depends_vehicle_traffic,
          depends_tourism: !!p.depends_tourism,
          depends_digital: !!p.depends_digital,
          known_competitors: Array.isArray(p.known_competitors) ? p.known_competitors.join(', ') : (p.known_competitors || ''),
          keywords: Array.isArray(p.keywords) ? p.keywords.join(', ') : (p.keywords || ''),
        });
        setAnalysisName(`${p.industry || 'Análisis'} — nuevo territorio`);
        localStorage.removeItem('strategia_prefill_profile');
        toast.info(`Perfil "${p.project_name}" precargado — completa la ubicación`);
      } catch (err) {
        // ignore malformed prefill payload
      }
    }
  }, []);

  const [munisFilter, setMunisFilter] = useState('');
  useEffect(() => {
    if (!stateCode) { setMunis([]); return; }
    api.get(`/catalog/municipalities/${stateCode}`).then((r) => setMunis(r.data));
    setSelectedMunis([]);
    setMunisFilter('');
  }, [stateCode]);

  const toggleMuni = (m) => {
    setSelectedMunis((prev) => {
      const exists = prev.find((x) => x.code === m.code);
      if (exists) return prev.filter((x) => x.code !== m.code);
      return [...prev, m];
    });
  };

  const applyRegion = (rid) => {
    setRegionId(rid);
    const r = regions.find((x) => x.id === rid);
    if (!r) return;
    setScope('region');
    setStateCode(r.state_codes[0] || '');
    // populate munis from region
    setTimeout(() => {
      const list = r.municipalities.map((m) => ({ code: m.muni_code, name: m.name, state_code: m.state_code }));
      setSelectedMunis(list);
    }, 400);
  };

  const validateStep = () => {
    if (step === 0) {
      if (!analysisName.trim()) return 'Escribe un nombre para el análisis';
      if (scope === 'direccion') {
        if (!addressPick) return 'Busca y selecciona una dirección';
        if (!addressPick.lat || !addressPick.lon) return 'La dirección seleccionada no tiene coordenadas';
      } else {
        if (!stateCode) return 'Selecciona un estado';
        if (selectedMunis.length === 0) return 'Selecciona al menos un municipio';
      }
    }
    if (step === 1) {
      if (!biz.project_name.trim()) return 'Nombre del proyecto requerido';
      if (!biz.company_name.trim()) return 'Nombre de la empresa requerido';
      if (!biz.industry.trim()) return 'Giro comercial requerido';
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      const profilePayload = {
        ...biz,
        avg_ticket: biz.avg_ticket ? Number(biz.avg_ticket) : null,
        gross_margin: biz.gross_margin ? Number(biz.gross_margin) : null,
        max_investment: biz.max_investment ? Number(biz.max_investment) : null,
        max_rent: biz.max_rent ? Number(biz.max_rent) : null,
        sqm_desired: biz.sqm_desired ? Number(biz.sqm_desired) : null,
        employees_estimated: biz.employees_estimated ? Number(biz.employees_estimated) : null,
        min_monthly_sales: biz.min_monthly_sales ? Number(biz.min_monthly_sales) : null,
        target_monthly_sales: biz.target_monthly_sales ? Number(biz.target_monthly_sales) : null,
        known_competitors: biz.known_competitors ? biz.known_competitors.split(',').map((s) => s.trim()).filter(Boolean) : [],
        keywords: biz.keywords ? biz.keywords.split(',').map((s) => s.trim()).filter(Boolean) : [],
        user_id: '',
      };

      // Scope=direccion → analysis-by-point (reverse geocode + AGEB pipeline)
      if (scope === 'direccion') {
        const r = await api.post('/analyses/point', {
          place_id: addressPick.place_id || null,
          lat: addressPick.lat,
          lon: addressPick.lon,
          place_name: addressPick.name || addressPick.address || analysisName,
          place_type: addressPick.type_detected || 'direccion',
          radius_km: Number(radius) || 3,
          business_profile: profilePayload,
          name: analysisName,
          notes,
        });
        toast.success('Análisis por dirección iniciado — reverse geocode + AGEBs');
        nav(`/analyses/${r.data.id}`);
        return;
      }

      const state = states.find((s) => s.code === stateCode);
      const payload = {
        name: analysisName,
        location: {
          scope,
          country: 'México',
          state_code: stateCode,
          state_name: state?.name || '',
          municipality_codes: selectedMunis.map((m) => m.code),
          municipality_names: selectedMunis.map((m) => m.name),
          region_name: regions.find((r) => r.id === regionId)?.name,
          radius_km: Number(radius) || 5,
          notes,
        },
        business_profile: profilePayload,
      };
      const r = await api.post('/analyses', payload);
      toast.success('Análisis iniciado — recopilando datos');
      nav(`/analyses/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear análisis');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-6xl">
      <div className="label-caps">Nuevo análisis</div>
      <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mt-2 mb-6">
        Crear análisis territorial
      </h1>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-8 flex-wrap">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = i === step;
          const done = i < step;
          return (
            <div key={s.key} data-testid={WIZARD.stepIndicator(i)} className="flex items-center gap-2">
              <div className={`flex items-center gap-2 px-3 py-2 border ${active ? 'border-blue-900 bg-blue-900 text-white' : done ? 'border-green-700 bg-green-50 text-green-800' : 'border-slate-200 bg-white text-slate-500'}`}>
                {done ? <CheckCircle2 size={14} /> : <Icon size={14} />}
                <span className="text-xs font-semibold uppercase tracking-widest">{i + 1}. {s.label}</span>
              </div>
              {i < STEPS.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 p-6 md:p-8">
        {/* STEP 0: Location */}
        {step === 0 && (
          <div className="space-y-6">
            <h3 className="font-display text-2xl font-bold tracking-tight">Ubicación</h3>

            <div>
              <Label>Nombre del análisis</Label>
              <Input value={analysisName} onChange={(e) => setAnalysisName(e.target.value)}
                     placeholder="Ej. Perfumería — Huatulco 2026" data-testid={WIZARD.analysisName} className="mt-1" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Tipo de análisis</Label>
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger data-testid={WIZARD.scopeSelect} className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ciudad">Ciudad</SelectItem>
                    <SelectItem value="municipio">Municipio</SelectItem>
                    <SelectItem value="zona_urbana">Zona urbana</SelectItem>
                    <SelectItem value="region">Región</SelectItem>
                    <SelectItem value="corredor">Corredor comercial</SelectItem>
                    <SelectItem value="direccion">Dirección + radio (punto)</SelectItem>
                    <SelectItem value="poligono" disabled>Polígono (próximamente)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Estado</Label>
                <Select value={stateCode} onValueChange={setStateCode} disabled={scope === 'direccion'}>
                  <SelectTrigger data-testid={WIZARD.stateSelect} className="mt-1"><SelectValue placeholder={scope === 'direccion' ? '(No aplica: se detecta al resolver la dirección)' : 'Selecciona estado'} /></SelectTrigger>
                  <SelectContent>
                    {states.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Radio (km)</Label>
                <Select value={String(radius)} onValueChange={(v) => setRadius(Number(v))}>
                  <SelectTrigger className="mt-1" data-testid="wizard-radius"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 5, 10, 15, 20].map((v) => <SelectItem key={v} value={String(v)}>{v} km</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {scope === 'direccion' && (
              <div className="border border-blue-200 bg-blue-50/30 p-4 space-y-3" data-testid="address-search-block">
                <div className="flex items-baseline justify-between">
                  <Label className="text-sm font-semibold text-blue-900">Dirección a analizar</Label>
                  <span className="text-[10px] uppercase tracking-widest text-blue-800 bg-blue-100 px-2 py-0.5">Punto + AGEBs INEGI</span>
                </div>
                <AddressPicker
                  value={addressPick}
                  onChange={(v) => {
                    setAddressPick(v);
                    if (v && !analysisName) setAnalysisName(`${biz.industry || 'Análisis'} — ${v.name || v.address || 'punto'}`);
                  }}
                  radiusKm={Number(radius) || 3}
                />
              </div>
            )}

            {scope === 'region' && (
              <div>
                <Label>Región curada</Label>
                <Select value={regionId} onValueChange={applyRegion}>
                  <SelectTrigger data-testid={WIZARD.regionSelect} className="mt-1"><SelectValue placeholder="Selecciona región" /></SelectTrigger>
                  <SelectContent>
                    {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {stateCode && scope !== 'direccion' && (
              <div>
                <Label>Municipios incluidos ({munis.length} disponibles)</Label>
                <Input
                  placeholder="Buscar municipio…"
                  value={munisFilter}
                  onChange={(e) => setMunisFilter(e.target.value)}
                  className="mt-2 mb-3 max-w-md"
                  data-testid="wizard-muni-search"
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 max-h-96 overflow-y-auto border border-slate-100 p-2">
                  {munis
                    .filter((m) => !munisFilter || m.name.toLowerCase().includes(munisFilter.toLowerCase()))
                    .slice(0, 300)
                    .map((m) => {
                      const checked = !!selectedMunis.find((x) => x.code === m.code);
                      return (
                        <label key={m.code} className={`flex items-center gap-2 p-2 border cursor-pointer transition-colors ${checked ? 'border-blue-900 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                          <Checkbox checked={checked} onCheckedChange={() => toggleMuni(m)} data-testid={WIZARD.muniCheckbox(m.code)} />
                          <span className="text-sm font-medium">{m.name}</span>
                        </label>
                      );
                    })}
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  {selectedMunis.length} municipio(s) seleccionados
                  {munis.filter((m) => !munisFilter || m.name.toLowerCase().includes(munisFilter.toLowerCase())).length > 300 && ' · Refina la búsqueda para ver más'}
                </div>
              </div>
            )}

            <div>
              <Label>Notas del usuario</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" placeholder="Cualquier información contextual del territorio o del proyecto." />
            </div>
          </div>
        )}

        {/* STEP 1: Business */}
        {step === 1 && (
          <div className="space-y-6">
            <h3 className="font-display text-2xl font-bold tracking-tight">Perfil del negocio</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Nombre del proyecto *</Label>
                <Input value={biz.project_name} onChange={(e) => setBiz({ ...biz, project_name: e.target.value })} data-testid={WIZARD.projectName} className="mt-1" /></div>
              <div><Label>Empresa *</Label>
                <Input value={biz.company_name} onChange={(e) => setBiz({ ...biz, company_name: e.target.value })} data-testid={WIZARD.companyName} className="mt-1" /></div>

              <div><Label>Giro comercial *</Label>
                <Input value={biz.industry} onChange={(e) => setBiz({ ...biz, industry: e.target.value })} data-testid={WIZARD.industry} placeholder="Ej. Perfumería" className="mt-1" /></div>
              <div><Label>Subcategoría</Label>
                <Input value={biz.subcategory} onChange={(e) => setBiz({ ...biz, subcategory: e.target.value })} placeholder="Ej. Lujo accesible" className="mt-1" /></div>

              <div><Label>Tipo de unidad</Label>
                <Select value={biz.unit_type} onValueChange={(v) => setBiz({ ...biz, unit_type: v })}>
                  <SelectTrigger data-testid={WIZARD.unitType} className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNIT_TYPES.map((u) => <SelectItem key={u.v} value={u.v}>{u.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>NSE objetivo</Label>
                <Input value={biz.socioeconomic} onChange={(e) => setBiz({ ...biz, socioeconomic: e.target.value })} placeholder="Ej. C+, B" className="mt-1" /></div>

              <div className="md:col-span-2"><Label>Descripción del negocio</Label>
                <Textarea value={biz.description} onChange={(e) => setBiz({ ...biz, description: e.target.value })} className="mt-1" /></div>

              <div><Label>Cliente objetivo</Label>
                <Input value={biz.target_customer} onChange={(e) => setBiz({ ...biz, target_customer: e.target.value })} className="mt-1" /></div>
              <div><Label>Rango de edad</Label>
                <Input value={biz.age_range} onChange={(e) => setBiz({ ...biz, age_range: e.target.value })} placeholder="Ej. 25-45" className="mt-1" /></div>

              <div><Label>Ticket promedio ($)</Label>
                <Input type="number" value={biz.avg_ticket} onChange={(e) => setBiz({ ...biz, avg_ticket: e.target.value })} data-testid={WIZARD.avgTicket} className="mt-1" /></div>
              <div><Label>Margen bruto (%)</Label>
                <Input type="number" value={biz.gross_margin} onChange={(e) => setBiz({ ...biz, gross_margin: e.target.value })} data-testid={WIZARD.margin} className="mt-1" /></div>

              <div><Label>Inversión máxima ($)</Label>
                <Input type="number" value={biz.max_investment} onChange={(e) => setBiz({ ...biz, max_investment: e.target.value })} data-testid={WIZARD.investment} className="mt-1" /></div>
              <div><Label>Renta mensual máx ($)</Label>
                <Input type="number" value={biz.max_rent} onChange={(e) => setBiz({ ...biz, max_rent: e.target.value })} className="mt-1" /></div>

              <div><Label>m² deseados</Label>
                <Input type="number" value={biz.sqm_desired} onChange={(e) => setBiz({ ...biz, sqm_desired: e.target.value })} className="mt-1" /></div>
              <div><Label>Empleados est.</Label>
                <Input type="number" value={biz.employees_estimated} onChange={(e) => setBiz({ ...biz, employees_estimated: e.target.value })} className="mt-1" /></div>

              <div><Label>Venta mensual mínima ($)</Label>
                <Input type="number" value={biz.min_monthly_sales} onChange={(e) => setBiz({ ...biz, min_monthly_sales: e.target.value })} data-testid={WIZARD.minSales} className="mt-1" /></div>
              <div><Label>Venta mensual objetivo ($)</Label>
                <Input type="number" value={biz.target_monthly_sales} onChange={(e) => setBiz({ ...biz, target_monthly_sales: e.target.value })} className="mt-1" /></div>

              <div className="md:col-span-2"><Label>Competidores conocidos (separados por coma)</Label>
                <Input value={biz.known_competitors} onChange={(e) => setBiz({ ...biz, known_competitors: e.target.value })} className="mt-1" /></div>
              <div className="md:col-span-2"><Label>Palabras clave (separadas por coma)</Label>
                <Input value={biz.keywords} onChange={(e) => setBiz({ ...biz, keywords: e.target.value })} data-testid={WIZARD.keywords} placeholder="perfume, cosmético, fragancia" className="mt-1" /></div>

              <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-3 pt-3 border-t border-slate-200">
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={biz.needs_parking} onCheckedChange={(v) => setBiz({ ...biz, needs_parking: !!v })} />Estacionamiento</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={biz.depends_foot_traffic} onCheckedChange={(v) => setBiz({ ...biz, depends_foot_traffic: !!v })} />Tráfico peatonal</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={biz.depends_vehicle_traffic} onCheckedChange={(v) => setBiz({ ...biz, depends_vehicle_traffic: !!v })} />Tráfico vehicular</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox data-testid={WIZARD.dependsTourism} checked={biz.depends_tourism} onCheckedChange={(v) => setBiz({ ...biz, depends_tourism: !!v })} />Dependencia turística</label>
                <label className="flex items-center gap-2 text-sm"><Checkbox checked={biz.depends_digital} onCheckedChange={(v) => setBiz({ ...biz, depends_digital: !!v })} />Venta digital</label>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Sources */}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-display text-2xl font-bold tracking-tight">Fuentes que se consultarán</h3>
            <p className="text-sm text-slate-600">La plataforma consulta fuentes públicas y algunas requieren credenciales. Google Maps/Places quedan preparados pero requieren API key propia (contacta al administrador).</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {sources.map((s) => (
                <div key={s.key} className="border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-slate-900">{s.name}</div>
                    <span className={`text-xs uppercase tracking-widest font-semibold px-2 py-0.5 ${s.status === 'disponible' ? 'bg-green-100 text-green-800' : s.status === 'no_configurado' ? 'bg-slate-200 text-slate-600' : 'bg-amber-100 text-amber-800'}`}>{s.status.replace('_', ' ')}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{s.note}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STEP 3: Confirm */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-display text-2xl font-bold tracking-tight">Confirmar y generar</h3>
            <div className="bg-slate-50 border border-slate-200 p-6 space-y-3">
              <div><span className="label-caps">Análisis</span><div className="font-semibold">{analysisName}</div></div>
              <div><span className="label-caps">Ubicación</span>
                {scope === 'direccion' ? (
                  <div>
                    {addressPick?.name || addressPick?.address || '—'} · <strong>{radius} km</strong> de radio
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {addressPick?.lat?.toFixed(6)}, {addressPick?.lon?.toFixed(6)} — estado + municipio detectados automáticamente
                    </div>
                  </div>
                ) : (
                  <div>{states.find((s) => s.code === stateCode)?.name} — {selectedMunis.map((m) => m.name).join(', ')} · <strong>{radius} km</strong></div>
                )}
              </div>
              <div><span className="label-caps">Giro</span><div>{biz.industry} · {biz.unit_type} · {biz.company_name}</div></div>
              <div><span className="label-caps">Ticket / Inversión</span><div>${biz.avg_ticket || '—'} / ${biz.max_investment || '—'}</div></div>
            </div>
            <div className="text-xs text-slate-500">Al confirmar, el análisis se ejecuta en background. Podrás ver el progreso en tiempo real en la siguiente pantalla.</div>
          </div>
        )}

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
          <Button variant="outline" onClick={prev} disabled={step === 0} data-testid={WIZARD.prevBtn}>
            <ChevronLeft size={14} className="mr-1" /> Anterior
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} data-testid={WIZARD.nextBtn} className="bg-blue-900 hover:bg-blue-800">
              Siguiente <ChevronRight size={14} className="ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={submitting} data-testid={WIZARD.submitBtn} className="bg-blue-900 hover:bg-blue-800">
              {submitting ? 'Iniciando análisis…' : 'Generar análisis territorial'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
